// VIBRANIAM-SPACE v2 - Engine.js
//
// One frame:
//   1. evaluate the signal graph N times (control rate)
//   2. resolve every render parameter through its modulation slots
//   3. push forces / emitter / audio into the substrates and step them
//   4. hand the layers to the pipeline
//
// dt is always 1/fps. Nothing reads a wall clock, so frame n is the same frame
// whether it was produced live or by the exporter.

import * as THREE from 'three';
import { GraphRuntime, EMPTY_FEATURES } from './evaluate.js';
import { Pipeline } from './render/Pipeline.js';
import { ParticleFieldSubstrate } from './substrates/ParticleFieldSubstrate.js';
import { GeometricMeshSubstrate } from './substrates/GeometricMeshSubstrate.js';
import { ImageStrobeSubstrate } from './substrates/ImageStrobeSubstrate.js';
import { MixerSwitcherSubstrate } from './substrates/MixerSwitcherSubstrate.js';
import { AudioNormalizer } from './audioNormalizer.js';
import { loadParticleField } from './wasm.js';

// Node types that produce a substrate. Both feed a layer through the same
// 'layer' port, so a layer can composite any mix of them.
const SUBSTRATE_TYPES = ['r.field', 'r.mesh', 'r.imageStrobe', 'r.mixer'];

const DEFAULT_LAYER = {
  opacity: 1, blend: 'add',
  feedback: 0.82, feedbackZoom: 1.004, feedbackRotate: 0.0012, feedbackWarp: 0.1,
  bloom: 0.85, bloomThreshold: 0.42, bloomRadius: 0.42,
};

const DEFAULT_OUTPUT = {
  width: 1920, height: 1080, fps: 60, duration: 12, crf: 16,
  exposure: 1, background: '#05060a', vignette: 0.35, grain: 0.015,
  stereo: 'off', stereoStrength: 0.7,
};

const DEFAULT_CAMERA = {
  distance: 5.5, fov: 55, orbit: true, orbitSpeed: 0.1, tilt: 0.15, roll: 0,
};

export class Engine {
  constructor({ renderer, width = 1920, height = 1080, controlSubsteps = 4 }) {
    this.renderer = renderer;
    this.width = width;
    this.height = height;
    this.controlSubsteps = Math.max(1, Math.min(16, controlSubsteps));

    this.runtime = new GraphRuntime();
    this.pipeline = new Pipeline(renderer, width, height);
    this.substrates = new Map();   // field node id -> substrate
    this.layerScenes = new Map();  // layer node id -> THREE.Scene
    this.camera = new THREE.PerspectiveCamera(55, width / height, 0.01, 2000);
    this.audioNormalizer = new AudioNormalizer(0.2, 0.95);

    this.frame = 0;
    this.modules = null;
    this.ready = false;
    this.error = null;
    this.lastStats = { alive: 0, capacity: 0, layers: 0, frame: 0, substrates: 0 };
  }

  async init() {
    try {
      const particleField = await loadParticleField();
      this.modules = { particleField };
      this.ready = true;
    } catch (err) {
      this.error = String(err?.message || err);
      throw err;
    }
    return this;
  }

  setGraph(graph) {
    this.runtime.setGraph(graph);

    // Retire substrates and layer scenes whose nodes are gone.
    const liveIds = new Set(
      SUBSTRATE_TYPES.flatMap((t) => this.runtime.nodesOfType(t).map((n) => n.id))
    );
    for (const [id, s] of [...this.substrates.entries()]) {
      if (!liveIds.has(id)) { s.dispose(); this.substrates.delete(id); }
    }
    const layerIds = new Set(this.runtime.nodesOfType('r.layer').map((n) => n.id));
    layerIds.add('__implicit__');
    for (const id of [...this.layerScenes.keys()]) {
      if (!layerIds.has(id)) this.layerScenes.delete(id);
    }
    this.pipeline.releaseUnused(layerIds);
  }

  resize(width, height) {
    this.width = Math.max(2, Math.floor(width));
    this.height = Math.max(2, Math.floor(height));
    this.pipeline.setSize(this.width, this.height);
    this.camera.aspect = this.width / this.height;
    this.camera.updateProjectionMatrix();
    for (const s of this.substrates.values()) s.setViewportHeight(this.height);
  }

  /** Back to frame 0: node state, substrate pools and trails all cleared. */
  reset() {
    this.frame = 0;
    this.runtime.resetState();
    for (const s of this.substrates.values()) s.reset();
    this.pipeline.resetFeedback();
  }

  outputParams() {
    const node = this.runtime.nodesOfType('r.output')[0];
    if (!node) return { ...DEFAULT_OUTPUT };
    return { ...DEFAULT_OUTPUT, ...this.runtime.resolveParams(node) };
  }

  cameraParams() {
    const outNode = this.runtime.nodesOfType('r.output')[0];
    let camNode = null;
    if (outNode) camNode = this.runtime.sourcesInto(outNode.id, 'camera')[0] || null;
    if (!camNode) camNode = this.runtime.nodesOfType('r.camera')[0] || null;
    if (!camNode) return { ...DEFAULT_CAMERA };
    return { ...DEFAULT_CAMERA, ...this.runtime.resolveParams(camNode) };
  }

  substrateFor(node) {
    let s = this.substrates.get(node.id);
    // A cached substrate of the wrong class would silently ignore the node's
    // parameters, so rebuild rather than reuse it.
    if (s && s.nodeType !== node.type) {
      s.dispose();
      this.substrates.delete(node.id);
      s = null;
    }
    if (!s) {
      if (node.type === 'r.mesh') {
        s = new GeometricMeshSubstrate(node.params || {});
      } else if (node.type === 'r.imageStrobe') {
        s = new ImageStrobeSubstrate(node.params || {});
      } else if (node.type === 'r.mixer') {
        s = new MixerSwitcherSubstrate(node.params || {});
      } else {
        const capacity = Number(node.params.capacity) || 60000;
        s = new ParticleFieldSubstrate(this.modules.particleField, capacity);
      }
      s.nodeType = node.type;
      s.setViewportHeight(this.height);
      this.substrates.set(node.id, s);
    }
    return s;
  }

  layerSceneFor(id) {
    let sc = this.layerScenes.get(id);
    if (!sc) { sc = new THREE.Scene(); this.layerScenes.set(id, sc); }
    return sc;
  }

  /**
   * Advances exactly one output frame.
   * @param {object} features analysis frame for this moment
   * @param {WebGLRenderTarget|null} target
   */
  step(features = EMPTY_FEATURES, target = null) {
    if (!this.ready) return this.lastStats;

    // Normalize audio features to prevent overexposure.
    const normalizedFeatures = this.audioNormalizer.process(features);

    const out = this.outputParams();
    const fps = Math.max(1, Math.round(out.fps || 60));
    const dt = 1 / fps;

    // 1. control-rate evaluation. An onset is a single event: it fires on the
    //    first substep only, otherwise one hit would trigger four times.
    const sub = this.controlSubsteps;
    const subDt = dt / sub;
    for (let i = 0; i < sub; i++) {
      this.runtime.evaluate({
        features: i === 0 ? normalizedFeatures : { ...normalizedFeatures, onset: false },
        dt: subDt,
        time: (this.frame + i / sub) * dt,
        frame: this.frame,
        fps,
        sampleRate: normalizedFeatures.sampleRate || 48000,
      });
    }

    // 2. substrates
    const fieldNodes = this.runtime.nodesOfType('r.field');
    const fieldsByLayer = new Map();
    let aliveTotal = 0;
    let capacityTotal = 0;

    /** Which layer consumes this substrate? Unwired ones still render. */
    const assignToLayer = (node, s) => {
      const layerNode = this.runtime.graph.edges
        .filter((e) => e.from.node === node.id && e.to.port === 'in')
        .map((e) => this.runtime.byId.get(e.to.node))
        .find((n) => n && n.type === 'r.layer');
      const key = layerNode ? layerNode.id : '__implicit__';
      if (!fieldsByLayer.has(key)) fieldsByLayer.set(key, []);
      fieldsByLayer.get(key).push(s);
    };

    for (const node of fieldNodes) {
      const p = this.runtime.resolveParams(node);
      const s = this.substrateFor(node);
      s.setCapacity(p.capacity);

      const forces = this.runtime.sourcesInto(node.id, 'forces')
        .filter((f) => f.type === 'r.force')
        .map((f) => {
          const fp = this.runtime.resolveParams(f);
          return {
            kind: Number(fp.kind) || 0,
            weight: Number(fp.weight) || 0,
            enabled: fp.enabled !== false,
            params: fp,
          };
        });

      s.applyForces(forces);
      s.applyEmitter(p);
      s.setAudio(features);
      s.step(dt, fps);

      aliveTotal += s.aliveCount;
      capacityTotal += s.capacity;
      assignToLayer(node, s);
    }

    // Geometric substrates: same lifecycle, no forces and no emitter pool -
    // their whole state is the resolved parameter set plus the analysis frame.
    for (const node of this.runtime.nodesOfType('r.mesh')) {
      const p = this.runtime.resolveParams(node);
      const s = this.substrateFor(node);
      s.applyEmitter(p);
      s.setAudio(features);
      s.step(dt, fps);

      aliveTotal += s.aliveCount;
      capacityTotal += s.capacity;
      assignToLayer(node, s);
    }

    // Image strobe substrates: no forces, no emitter, just image cycling
    for (const node of this.runtime.nodesOfType('r.imageStrobe')) {
      const p = this.runtime.resolveParams(node);
      const s = this.substrateFor(node);
      s.setParams(p);
      s.setViewport(this.width, this.height);
      s.setAudio(features);
      s.step(dt, fps);

      aliveTotal += 1; // counts as one substrate
      capacityTotal += 1;
      assignToLayer(node, s);
    }

    // Mixer / switcher: hard cuts between strobe textures, generated
    // backgrounds and the live animation (the field shows through the layer).
    for (const node of this.runtime.nodesOfType('r.mixer')) {
      const p = this.runtime.resolveParams(node);
      const s = this.substrateFor(node);
      s.setParams(p);
      s.setViewport(this.width, this.height);
      // Collect current textures from any connected image strobe nodes.
      const strobeTextures = this.runtime.sourcesInto(node.id, 'strobe')
        .filter((n) => n.type === 'r.imageStrobe')
        .map((n) => this.substrates.get(n.id)?.getCurrentTexture())
        .filter(Boolean);
      s.setStrobeTextures(strobeTextures);
      s.setAudio(features);
      s.step(dt, fps);

      aliveTotal += 1;
      capacityTotal += 1;
      assignToLayer(node, s);
    }

    // 3. camera
    const cp = this.cameraParams();
    const time = this.frame / fps;
    if (this.camera.fov !== cp.fov) {
      this.camera.fov = cp.fov;
      this.camera.updateProjectionMatrix();
    }
    const angle = cp.orbit ? time * cp.orbitSpeed * Math.PI * 2 : 0;
    const r = Math.max(0.2, cp.distance);
    this.camera.position.set(
      Math.sin(angle) * r,
      Math.sin(angle * 0.5) * r * cp.tilt,
      Math.cos(angle) * r
    );
    this.camera.lookAt(0, 0, 0);
    if (cp.roll) this.camera.rotateZ(cp.roll);

    // 4. layers
    const layerNodes = [...this.runtime.nodesOfType('r.layer')].sort((a, b) => a.x - b.x);
    const layers = [];

    const buildLayer = (id, params, substrates) => {
      const scene = this.layerSceneFor(id);
      // Sync the scene's children with this layer's substrates.
      const wanted = new Set(substrates.map((s) => s.points));
      for (const child of [...scene.children]) if (!wanted.has(child)) scene.remove(child);
      for (const s of substrates) {
        s.setViewportHeight(this.height);
        if (s.points.parent !== scene) scene.add(s.points);
      }
      layers.push({ id, scene, camera: this.camera, params });
    };

    for (const node of layerNodes) {
      const p = { ...DEFAULT_LAYER, ...this.runtime.resolveParams(node) };
      buildLayer(node.id, p, fieldsByLayer.get(node.id) || []);
    }
    // Fields not wired to a layer still render, so the patch is never blank
    // while you are in the middle of connecting it.
    if (fieldsByLayer.has('__implicit__')) {
      buildLayer('__implicit__', { ...DEFAULT_LAYER }, fieldsByLayer.get('__implicit__'));
    }

    // 5. render
    this.pipeline.render(layers, out, this.frame, fps, target);
    this.frame++;

    this.lastStats = {
      alive: aliveTotal,
      capacity: capacityTotal,
      layers: layers.length,
      substrates: this.substrates.size,
      frame: this.frame,
      fps,
      cyclic: this.runtime.cyclic,
    };
    return this.lastStats;
  }

  dispose() {
    for (const s of this.substrates.values()) s.dispose();
    this.substrates.clear();
    this.layerScenes.clear();
    this.pipeline.dispose();
  }
}

export { DEFAULT_LAYER, DEFAULT_OUTPUT, DEFAULT_CAMERA };
